import { AppPageUrl, rootPageUrl } from "@common";
import { AppState, campaignBudgetForm, campaignCreativesForm, campaignMediaPlanForm } from "@store";
import { createPath } from "rd-url-utils";
import { Store } from "redux";
import { config } from "./config";

interface TokenInfo {
    auth_token: string;
    refresh_token: string;
}

const TOKEN_STORAGE_KEY = "acd.tokens";

export const StoreTokenUrl = createPath("/signed-in");

export function apiUrl(path: string): ((params: any) => Promise<string>) {
    return _ => config.then(c => `${(c.apiUrl || "").replace(/\/$/, "")}/${(path || "").replace(/^\//, "")}`);
}

export function customFetch(request: Request): Promise<Response> {
    request = prepareRequest(request);

    return fetch(request).then(
        (res: Response) => {
            if (res.status === 401 || res.status === 403) {
                return redirectToLogin() as any;
            }

            return res;
        },
        err => {
            // TODO: This is not actually correct, just leave it
            // before handling authorization on server side.
            // redirectToLogin();
            return Promise.reject(err);
        }
    );
}

export function getTokens(): TokenInfo | undefined {
    const tokenStr = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (tokenStr) {
        return JSON.parse(tokenStr);
    }

    return undefined;
}

export function storeTokens(tokens: TokenInfo): void {
    const tokenStr = JSON.stringify(tokens);
    localStorage.setItem(TOKEN_STORAGE_KEY, tokenStr);
}

export function loginUrl(): Promise<string> {
    return config.then(c => {
        const params = {
            redirect_uri: window.location.origin + StoreTokenUrl.format({}),
            client_id: c.loginClientId,
            scope: "openid email profile",
            response_type: "token",
            state: rootPageUrl.match(window.location.pathname, true).isMatched
                ? AppPageUrl.format({}).toString()
                : window.location.toString()
        };

        const querystring = Object.keys(params)
            .map(k => `${k}=${encodeURIComponent(params[k])}`)
            .join("&");

        return `${c.loginServerUrl}?${querystring}`;
    });
}

export function logoutUrl(): Promise<string> {
    return config.then(c => {
        const params = {
            logout_uri: window.location.origin + "/signed-out",
            client_id: c.loginClientId
        };

        const querystring = Object.keys(params)
            .map(k => `${k}=${encodeURIComponent(params[k])}`)
            .join("&");

        return `${c.logoutServerUrl}?${querystring}`;
    });
}

export function prepareRequest(request: Request): Request {
    const tokens = getTokens();

    const authHeader = tokens && tokens.auth_token ? { Authorization: `${tokens.auth_token}` } : {};

    return new Request(request, {
        mode: "cors",
        cache: "no-cache",
        headers: {
            ...authHeader,
            Accept: "application/json",
            "Content-Type": "application/json"
        }
    });
}

export function prepareFormDataRequest(request: Request): Request {
    const tokens = getTokens();

    const authHeader = tokens && tokens.auth_token ? { Authorization: `${tokens.auth_token}` } : {};

    return new Request(request, {
        mode: "cors",
        cache: "no-cache",
        headers: {
            ...authHeader,
            Accept: "image/*",
            "Content-Type": "multipart/form-data"
        }
    });
}

let store: Store<AppState> | undefined = undefined;

export function redirectToLogin(): Promise<void> {
    if (store) {
        const state = store.getState();

        saveCampaignData(state);
    }
    return loginUrl().then(url => window.location.assign(url));
}

export function saveStore(storeToSave: Store<AppState>): void {
    store = storeToSave;
}

export interface CampaignEditingData {
    savedAt: number;
    mediaPlan: typeof campaignMediaPlanForm.types.state;
    budget: typeof campaignBudgetForm.types.state;
    creatives: typeof campaignCreativesForm.types.state;
}

const CAMPAIGN_DATA_KEY = "acd.campaign-data";

function saveCampaignData(state: AppState) {
    const { campaignMediaPlanForm, campaignBudgetForm, campaignCreativesForm } = state.campaignState;

    const data: CampaignEditingData = {
        savedAt: new Date().getTime(),
        mediaPlan: campaignMediaPlanForm,
        budget: campaignBudgetForm,
        creatives: campaignCreativesForm
    };

    localStorage.setItem(CAMPAIGN_DATA_KEY, JSON.stringify(data));
}

export function loadCampaignData(): CampaignEditingData | undefined {
    const data = localStorage.getItem(CAMPAIGN_DATA_KEY);
    if (!data) {
        return undefined;
    }

    const result = JSON.parse(data) as CampaignEditingData;
    // const now = new Date().getTime();

    // Saved data valid for 10 minutes
    // if (Math.abs(now - result.savedAt) > 10 * 60 * 1000) {
    //     return undefined;
    // }

    return result;
}

export function resetCampaignData() {
    localStorage.removeItem(CAMPAIGN_DATA_KEY);
}
